import {useState} from 'react';
import {ChevronDownIcon, ChevronUpIcon, RefreshCwIcon} from 'lucide-react';
import {
  BadgeStatus,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@tinct/web-components';
import {cn} from '@tinct/core-utils';

export type SnippetStatus = 'active' | 'not_detected' | 'on_error';

export type SnippetCampaign = {
  name: string;
  url: string;
  status: 'active' | 'inactive';
};

export type SnippetStatusCardProps = {
  className?: string;
  status: SnippetStatus;
  campaigns?: SnippetCampaign[];
  lastVerified?: Date | string | null;
  activeSince?: Date | string | null;
  domain?: string | null;
  onVerifyAgain?: () => void;
  isVerifying?: boolean;
};

type VisualState = 'ok' | 'error-snippet' | 'error-campaign';

function resolveVisualState(
  status: SnippetStatus,
  campaigns?: SnippetCampaign[],
): VisualState {
  if (status === 'not_detected' || status === 'on_error') return 'error-snippet';
  if (campaigns?.some((c) => c.status === 'inactive')) return 'error-campaign';
  return 'ok';
}

const VISUAL_CONFIG: Record<
  VisualState,
  {
    badgeVariant: 'green' | 'red' | 'yellow';
    badgeLabel: string;
    cardClassName: string;
    description: [string, string];
  }
> = {
  ok: {
    badgeVariant: 'green',
    badgeLabel: 'Active',
    cardClassName: 'bg-[var(--colors-success-background)] border-teal-800',
    description: [
      'Your snippet is active on your domain ',
      "and every page you're using in your campaigns",
    ],
  },
  'error-snippet': {
    badgeVariant: 'red',
    badgeLabel: 'Inactive',
    cardClassName: 'bg-muted border-rose-500',
    description: [
      'Your snippet is not responding. ',
      'Reinstall it below or contact support if the issue persists.',
    ],
  },
  'error-campaign': {
    badgeVariant: 'yellow',
    badgeLabel: 'Partially active',
    cardClassName: 'bg-muted border-amber-500',
    description: [
      'Your snippet is active on your domain but is missing on some pages you use in your campaigns.',
      'Install globally or contact support if the issue persists.',
    ],
  },
};

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function SnippetStatusCard({
  className,
  status,
  campaigns = [],
  lastVerified,
  activeSince,
  domain,
  onVerifyAgain,
  isVerifying = false,
}: SnippetStatusCardProps) {
  const [expanded, setExpanded] = useState(false);
  const visualState = resolveVisualState(status, campaigns);
  const config = VISUAL_CONFIG[visualState];


  if(status === 'not_detected' && !lastVerified) {
    return ;
  }

  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl border',
        config.cardClassName,
        className,
      )}
    >
      {/* Header */}
      <div className="px-6 pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-xl font-semibold leading-7 tracking-[-0.3px] text-foreground">
                Snippet Status
              </h3>
              <BadgeStatus variant={config.badgeVariant}>
                {config.badgeLabel}
              </BadgeStatus>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onVerifyAgain}
              disabled={isVerifying}
              isLoading={isVerifying}
            >
              <RefreshCwIcon className="size-4" />
              Verify again
            </Button>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="px-6 py-6">
        <p className="text-sm text-card-foreground">
          {config.description[0]}
          <br />
          {config.description[1]}
        </p>
      </div>

      {/* Stats row */}
      <div className="border-t px-6 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex w-24 flex-col gap-2">
              <span className="text-sm text-muted-foreground">
                Last verified
              </span>
              <span className="text-base font-semibold leading-none text-card-foreground">
                {formatDate(lastVerified)}
              </span>
            </div>
            <div className="flex w-24 flex-col gap-2">
              <span className="text-sm text-muted-foreground">
                Active since
              </span>
              <span className="text-base font-semibold leading-none text-card-foreground">
                {visualState === 'error-snippet'
                  ? '-'
                  : formatDate(activeSince)}
              </span>
            </div>
            <div className="flex w-[141px] flex-col gap-2">
              <span className="text-sm text-muted-foreground">Domain</span>
              {domain ? (
                <span className="text-base font-semibold leading-none text-card-foreground underline">
                  {domain}
                </span>
              ) : (
                <span className="text-base font-semibold leading-none text-card-foreground">
                  -
                </span>
              )}
            </div>
          </div>
          {campaigns.length > 0 && (
            <Button variant="ghost" onClick={() => setExpanded(!expanded)}>
              {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
              {expanded ? (
                <ChevronDownIcon className="size-4" />
              ) : (
                <ChevronUpIcon className="size-4" />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Expanded campaign detail table */}
      {expanded && campaigns.length > 0 && (
        <div className="px-6 pb-6">
          <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead className="min-w-[121px]">
                    Snippet status
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={`${campaign.name}-${campaign.url}`}>
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell>{campaign.url}</TableCell>
                    <TableCell>
                      <BadgeStatus
                        variant={
                          campaign.status === 'active' ? 'green' : 'red'
                        }
                      >
                        {campaign.status === 'active' ? 'Active' : 'Inactive'}
                      </BadgeStatus>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
